import React, { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";
import { useEscapeToClose, useAutoFocusOnOpen } from "@/lib/a11y";

// Bump CHANGELOG_VERSION whenever you ship a release worth surfacing.
// On the next login, every authenticated user whose stored
// `ccs_seen_changelog_version` is older (or absent) sees the popover
// once. After they tap "Got it" the new version is written back.
//
// Keep the entries short and outcome-focused — what the user can DO,
// not implementation detail.
export const CHANGELOG_VERSION = "2026.02.b";

export const CHANGELOG_ENTRIES = [
  {
    title: "Public share link, WhatsApp & QR",
    body: "Settings → Public results link now has a one-tap WhatsApp share button and a downloadable QR code for slides and noticeboards.",
  },
  {
    title: "Bottom navigation on phones",
    body: "The sidebar moves to the bottom on phones with a More menu — designed for one-thumb use during a club night.",
  },
  {
    title: "Refined competition types",
    body: "Competition Type now offers PDI Colour, PDI Mono, Print Colour, Print Mono, Themed and Open — clearer for everyone.",
  },
  {
    title: "Viewer-friendly statistics",
    body: "Statistics and Season Stats are hidden from Viewer accounts; admins and secretaries keep full access.",
  },
  {
    title: "Keyboard shortcuts",
    body: "Press ? to see them. g h goes to Dashboard, g c to Competitions, g r to Results, and more.",
  },
];

const STORAGE_KEY = "ccs_seen_changelog_version";

/**
 * One-time-per-version "What's new" popover. Renders nothing if the
 * user has already acknowledged the current CHANGELOG_VERSION. Mounted
 * inside Layout, so it only appears for authenticated users.
 */
export default function WhatsNewPopover() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    let seen = "";
    try {
      seen = localStorage.getItem(STORAGE_KEY) || "";
    } catch (_) {
      return; // private mode → silent skip
    }
    if (seen !== CHANGELOG_VERSION) setShow(true);
  }, []);

  const dismiss = () => {
    setShow(false);
    try {
      localStorage.setItem(STORAGE_KEY, CHANGELOG_VERSION);
    } catch (_) {
      // ignore — popover is hidden either way
    }
  };

  useEscapeToClose(show ? dismiss : null);
  const gotItRef = useAutoFocusOnOpen(show);

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.65)" }}
      onClick={dismiss}
      data-testid="whats-new-backdrop"
    >
      <div
        className="w-full max-w-md rounded-2xl border shadow-2xl"
        style={{ background: "#0d0d0d", borderColor: "#1f1f1f" }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="whats-new-title"
        data-testid="whats-new-popover"
      >
        <div
          className="flex items-center gap-3 px-5 py-4 border-b"
          style={{ borderColor: "#1f1f1f" }}
        >
          <span
            className="inline-flex items-center justify-center w-9 h-9 rounded-full"
            style={{ background: "#11061d", color: "#fbbf24" }}
            aria-hidden="true"
          >
            <Sparkles size={18} />
          </span>
          <div className="flex-1 min-w-0">
            <div className="mono text-[10px] uppercase tracking-[0.3em] text-amber-400">
              Release {CHANGELOG_VERSION}
            </div>
            <div id="whats-new-title" className="text-base font-semibold tracking-tight">
              What&rsquo;s new
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="p-1 text-neutral-400 hover:text-white"
            aria-label="Close"
            data-testid="whats-new-close"
          >
            <X size={16} />
          </button>
        </div>
        <ul
          className="px-5 py-4 space-y-4 max-h-[60vh] overflow-y-auto"
          data-testid="whats-new-list"
        >
          {CHANGELOG_ENTRIES.map((e, i) => (
            <li key={i} className="flex items-start gap-3">
              <span
                className="shrink-0 mt-1 inline-block w-1.5 h-1.5 rounded-full"
                style={{ background: "#fbbf24" }}
              />
              <div className="min-w-0">
                <div className="text-sm font-medium">{e.title}</div>
                <div className="text-xs text-neutral-300 mt-0.5">{e.body}</div>
              </div>
            </li>
          ))}
        </ul>
        <div className="px-5 py-4 border-t" style={{ borderColor: "#1f1f1f" }}>
          <button
            ref={gotItRef}
            type="button"
            onClick={dismiss}
            className="ccs-btn-primary w-full justify-center text-sm"
            data-testid="whats-new-got-it"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
